import { FaSolidArrowsRotate } from 'solid-icons/fa'
import { Component, createMemo, createSignal, Match, Show, Switch, untrack, useContext } from 'solid-js'
import type { Engine } from '../../lib/core/engine/Engine.type'
import getPlayerGraphNode from '../../lib/core/engine/PlayerGraphNode/getPlayerGraphNode'
import getPerTurnIncomeFromPlayerEnts from '../../lib/core/entity/getPerTurnIncomeFromPlayerEnts'
import findById from '../../lib/core/findById'
import type { Player } from '../../lib/core/player/Player.type'
import playerDisplayName from '../../lib/core/player/playerDisplayName'
import { PlayerGameStatus } from '../../lib/core/player/PlayerGameStatus'
import { createEndTurnAction } from '../../lib/core/state/flux/action/Game/EndTurnAction'
import dispatchClient from '../../lib/core/state/flux/dispatchClient'
import { InputActionType } from '../../lib/event/InputRouter/InputActionType'
import KeyboardShortcut from '../../lib/event/InputRouter/KeyboardShortcut.component'
import { mountInputActionListener } from '../../lib/event/InputRouter/mountInputActionListener'
import withBusy from '../../lib/withBusy'
import { useAuthPlayerIdSignal } from '../../rx/signal/useAuthPlayerIdSignal'
import type { Nullable } from '../../typescript'
import PlayerColorsBar from '../ArmyPainter/PlayerColorsBar'
import PlayerGameStatusBadge from '../Badge/PlayerGameStatusBadge'
import EngineContext from '../EngineContext'
import PerTurnIncome from './PerTurnIncome'
import PlayerMoneyCornerWidget from './PlayerMoneyCornerWidget'
import { EventResult } from '../../lib/event/event'

const PlayerCornerWidget: Component = () => {
  const engine: Engine = useContext(EngineContext)
  const [getAuthPlayerId] = useAuthPlayerIdSignal(engine)
  
  const isAuthTurn = createMemo<boolean>((): boolean => {
    const authPlayerId = getAuthPlayerId()
    return (authPlayerId && authPlayerId === engine.state.turnPlayerId) as boolean
  })

  const turnPlayer = createMemo<Nullable<Player>>((): Nullable<Player> => {
    const { players, turnPlayerId } = engine.state
    return findById(players, turnPlayerId)
  })
  const canEndTurn = createMemo<boolean>((): boolean => {
    return isAuthTurn() && true
  })

  const [isBusy, setIsBusy] = createSignal<boolean>(false)
  const [isMoneyWidgetOpen, setIsMoneyWidgetOpen] = createSignal<boolean>(false)

  return (
    <Show when={!engine.draftMove && getAuthPlayerId()} keyed children={(authPlayerId) => {
      const player = getPlayerGraphNode(engine, authPlayerId)
    
      const perTurnIncome = createMemo<number>(() => {
        return getPerTurnIncomeFromPlayerEnts(player.ents())
      })

      return <>
        <div class="cw cw-player cw-sm-br">
          <div class="text-break text-monospace">
            <PlayerColorsBar value={player()} width={16} height={16} />&nbsp;
            {playerDisplayName(player())}
          </div>
          <div class="d-flex justify-content-between">
            <Show when={player().game_status !== PlayerGameStatus.Active}>
              <PlayerGameStatusBadge player={player()} />
            </Show>
            <Switch>
              <Match when={engine.state.ended}>
                <span class="fs-6">GGHF</span>
              </Match>
              <Match when={isAuthTurn()}>
                <span class="fs-6">Your Turn</span>
                <Show when={canEndTurn()} children={(
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  canEndTurn) => {

                  const doEndTurn = withBusy(isBusy, setIsBusy, async () => {
                    // console.log('doEndTurn')
                    // computations created outside a `createRoot` or `render` will never be disposed
                    const playerId = untrack(() => player().id)
                    await dispatchClient(engine, createEndTurnAction(playerId))
                  })

                  mountInputActionListener(InputActionType.EndTurn, () => {
                    console.log('mountInputActionListener(InputActionType.EndTurn)')
                    doEndTurn()
                    return EventResult.Stop
                  })

                  return (
                    <button
                      class="btn btn-danger btn-sm"
                      classList={{ 'disabled': isBusy() }}
                      disabled={isBusy()}
                      onClick={doEndTurn}
                    ><FaSolidArrowsRotate />{' '}End Turn <KeyboardShortcut action={InputActionType.EndTurn} icon />
                    </button>)
                }}
                />
              </Match>
              <Match when={turnPlayer()}>
                <span class="fs-6 text-muted ellipsis-animated">Waiting on other player's turn</span>
              </Match>
            </Switch>
          </div>
          <PerTurnIncome value={perTurnIncome()} player={player()} onClick={() => {
            setIsMoneyWidgetOpen(!isMoneyWidgetOpen())
          }} />
        </div>
        <PlayerMoneyCornerWidget
          class="player1 cw-sm-br"
          when={isMoneyWidgetOpen()}
          setOpen={setIsMoneyWidgetOpen}
          player={player}
        />
      </>
    }} />
  )
}

export default PlayerCornerWidget
